import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { db, doc, onSnapshot, Timestamp } from "../../../firebase";

const OFFLINE_AFTER_MS = 21000;

const INITIAL = {
  canIds: [],
  soc: [],
  voltage: [],
  doorStatus: [],
  syncTimestamp: null,
  lastSeen: null,
  isOnline: false,
  isReady: false,
  error: null,
};

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts.seconds === "number") {
    return new Timestamp(ts.seconds, ts.nanoseconds ?? 0).toDate();
  }
  return null;
};

export default function useStationStatus(deviceId) {
  const [station, setStation] = useState(INITIAL);
  const offlineTimeoutRef = useRef(null);
  const latestHeartbeatRef = useRef(null);

  useEffect(() => {
    setStation(INITIAL);
    latestHeartbeatRef.current = null;

    if (!deviceId) return undefined;

    const clearOfflineTimer = () => {
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
        offlineTimeoutRef.current = null;
      }
    };

    const scheduleOffline = (heartbeatMs) => {
      clearOfflineTimer();
      latestHeartbeatRef.current = heartbeatMs;

      const delay = Math.max(0, heartbeatMs + OFFLINE_AFTER_MS - Date.now());

      offlineTimeoutRef.current = setTimeout(() => {
        if (latestHeartbeatRef.current !== heartbeatMs) return;
        setStation((prev) => ({ ...prev, isOnline: false }));
      }, delay);
    };

    const documentRef = doc(db, "devices", deviceId, "SwapStn", "Status");

    const unsubscribe = onSnapshot(
      documentRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          clearOfflineTimer();
          latestHeartbeatRef.current = null;
          setStation({ ...INITIAL, isReady: true, error: "Device not found" });
          return;
        }

        const data = snapshot.data();
        const state = data?.state ?? {};

        const heartbeat = toDate(
          state.timeStamp ?? state.createdAt ?? data.createdAt,
        );

        const base = {
          canIds: state.canId || [],
          soc: state.soc || [],
          voltage: state.voltage || [],
          doorStatus: state.door_status_1 || [],
          chargerStatus: state.charger_status_1 || [],
          eS: state.eS || [],
          isReady: true,
          error: null,
        };

        if (!heartbeat) {
          clearOfflineTimer();
          latestHeartbeatRef.current = null;
          setStation({
            ...base,
            syncTimestamp: null,
            lastSeen: null,
            isOnline: false,
          });
          return;
        }

        const heartbeatMs = heartbeat.getTime();

        const isFresh = Date.now() - heartbeatMs < OFFLINE_AFTER_MS;

        setStation({
          ...base,
          syncTimestamp: format(heartbeat, "hh:mm:ss a dd/MM/yyyy"),
          lastSeen: heartbeat,
          isOnline: isFresh,
        });

        if (isFresh) {
          scheduleOffline(heartbeatMs);
        } else {
          clearOfflineTimer();
          latestHeartbeatRef.current = heartbeatMs;
        }
      },
      (err) => {
        console.error("Station status listener failed:", err);
        clearOfflineTimer();
        latestHeartbeatRef.current = null;
        setStation((prev) => ({
          ...prev,
          isOnline: false,
          isReady: true,
          error: err.message,
        }));
      },
    );

    return () => {
      unsubscribe();
      clearOfflineTimer();
      latestHeartbeatRef.current = null;
    };
  }, [deviceId]);

  return station;
}
